import React from 'react';
import { Link } from 'react-router-dom';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import SEO, { createBreadcrumbSchema, createFAQSchema, softwareApplicationSchema } from '@/components/SEO';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { ArrowRight, CheckCircle, Zap, Image, Package, ShoppingBag, Layers, Clock, Star, Award } from 'lucide-react';

const AIImageGeneratorAmazonUK = () => {
  const faqs = [
    {
      question: "What are Amazon UK's image requirements?",
      answer: "Amazon.co.uk requires main images to be on a pure white background (RGB 255,255,255), at least 1000x1000 pixels for zoom functionality. Our AI generator creates images that meet all Amazon UK's strict image standards."
    },
    {
      question: "Can I create A+ Content images for Amazon UK?",
      answer: "Absolutely! Generate stunning A+ Content images including comparison charts, lifestyle photos, infographics, and brand story visuals. Perfect for enhancing your Amazon UK product detail pages."
    },
    {
      question: "Are AI-generated images compliant with Amazon UK policies?",
      answer: "Yes, AI-generated images are allowed for product photography on Amazon. All images include full commercial rights. However, ensure you accurately represent your products as per Amazon's guidelines."
    },
    {
      question: "How can I optimise images for Amazon UK search?",
      answer: "Our AI creates high-quality images with the right dimensions and clarity for Amazon's search algorithm. Well-optimised product images can improve your listing's visibility and conversion rates on Amazon.co.uk."
    },
    {
      question: "Do you support Brand Registry sellers on Amazon UK?",
      answer: "Yes! We help Brand Registry sellers create enhanced brand content, storefront images, and A+ Content visuals. Generate professional imagery that elevates your brand presence on Amazon UK."
    }
  ];

  const features = [
    {
      icon: <Image className="w-6 h-6" />,
      title: "Amazon UK Compliant",
      description: "Generate images meeting Amazon.co.uk's strict requirements: white backgrounds, 1000px+ dimensions"
    },
    {
      icon: <Package className="w-6 h-6" />,
      title: "A+ Content Ready",
      description: "Create stunning enhanced brand content, comparison charts, and lifestyle imagery"
    },
    {
      icon: <ShoppingBag className="w-6 h-6" />,
      title: "Main Image Quality",
      description: "Professional product photos with pure white backgrounds that pass Amazon's image checks"
    },
    {
      icon: <Layers className="w-6 h-6" />,
      title: "Multi-Image Listings",
      description: "Generate all 7-9 listing images: main, angles, lifestyle, scale, and infographics"
    },
    {
      icon: <Zap className="w-6 h-6" />,
      title: "Bulk Generation",
      description: "Create entire product catalogues of images in minutes, not weeks"
    },
    {
      icon: <Award className="w-6 h-6" />,
      title: "UK Market Optimised",
      description: "Images styled for UK consumer preferences and Amazon.co.uk marketplace"
    }
  ];

  const useCases = [
    "Amazon FBA sellers launching on Amazon UK marketplace",
    "UK-based private label brands expanding catalogues",
    "Wholesale sellers needing consistent product imagery",
    "Brand Registry sellers creating A+ Content",
    "Arbitrage sellers requiring professional photos quickly",
    "Multi-channel sellers listing on Amazon UK",
    "Seasonal product launches for UK holidays",
    "Premium brand sellers on Amazon.co.uk"
  ];

  return (
    <>
      <SEO
        title="AI Image Generator for Amazon UK | Amazon.co.uk Product Photos"
        description="Create Amazon UK compliant product photos with AI. White backgrounds, A+ Content, lifestyle images for Amazon.co.uk sellers. Used by 5,000+ UK Amazon sellers."
        keywords="ai image generator amazon uk, amazon.co.uk product photos, amazon uk listing images, amazon uk seller photography, amazon uk fba images, amazon uk a+ content, amazon uk product photography, amazon uk white background photos"
        url="/ai-image-generator-amazon-uk"
        jsonLd={[
          softwareApplicationSchema,
          createBreadcrumbSchema([
            { name: 'Home', url: '/' },
            { name: 'AI Image Generator for Amazon UK', url: '/ai-image-generator-amazon-uk' }
          ]),
          createFAQSchema(faqs)
        ]}
      />
      
      <Header />
      
      <main className="min-h-screen bg-background">
        {/* Hero Section */}
        <section className="relative py-20 overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-orange-500/10 via-amber-500/5 to-background" />
          <div className="container mx-auto px-4 relative z-10">
            <div className="max-w-4xl mx-auto text-center">
              <Badge className="mb-4 bg-gradient-to-r from-orange-500/20 to-amber-500/20 text-foreground border-0">
                Trusted by 5,000+ Amazon UK Sellers
              </Badge>
              <h1 className="text-4xl md:text-6xl font-bold mb-6">
                AI Image Generator for Amazon UK
                <span className="block bg-gradient-to-r from-orange-400 to-amber-400 bg-clip-text text-transparent drop-shadow-sm">
                  Amazon.co.uk Product Photos
                </span>
              </h1>
              <p className="text-xl text-muted-foreground mb-8 max-w-2xl mx-auto">
                Create Amazon UK compliant product images with AI. Professional white backgrounds, 
                A+ Content visuals, and lifestyle photography in seconds. Made for UK Amazon sellers.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button asChild size="lg" className="bg-gradient-to-r from-orange-500 to-amber-500 hover:opacity-90 text-white">
                  <Link to="/try-generator">
                    Create Amazon UK Images Free <ArrowRight className="ml-2 h-5 w-5" />
                  </Link>
                </Button>
                <Button asChild size="lg" variant="outline">
                  <Link to="/gallery">View Product Examples</Link>
                </Button>
              </div>
              
              {/* Trust Badges */}
              <div className="mt-8 flex flex-wrap justify-center gap-6 text-sm text-muted-foreground">
                <div className="flex items-center gap-2">
                  <Star className="w-4 h-4 text-yellow-500 fill-yellow-500" />
                  <span>4.9/5 from UK sellers</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green-500" />
                  <span>Amazon UK Compliant</span>
                </div>
                <div className="flex items-center gap-2">
                  <Zap className="w-4 h-4 text-amber-500" />
                  <span>Full Commercial Rights</span>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Features Grid */}
        <section className="py-16 bg-muted/30">
          <div className="container mx-auto px-4">
            <h2 className="text-3xl font-bold text-center mb-12">
              Why UK Amazon Sellers Choose Our AI Generator
            </h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
              {features.map((feature, index) => (
                <Card key={index} className="border border-transparent bg-gradient-to-br from-orange-500/5 to-amber-500/5 hover:from-orange-500/10 hover:to-amber-500/10 hover:border-orange-500/20 transition-all shadow-sm hover:shadow-md">
                  <CardContent className="p-6">
                    <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-orange-500/20 to-amber-500/20 flex items-center justify-center text-foreground mb-4">
                      {feature.icon}
                    </div>
                    <h3 className="text-lg font-semibold mb-2">{feature.title}</h3>
                    <p className="text-muted-foreground">{feature.description}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* Use Cases */}
        <section className="py-16">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-3xl font-bold text-center mb-12">
                Perfect For Every UK Amazon Seller
              </h2>
              <div className="grid md:grid-cols-2 gap-4">
                {useCases.map((useCase, index) => (
                  <div key={index} className="flex items-center gap-3 p-4 rounded-lg bg-gradient-to-r from-orange-500/5 to-amber-500/5">
                    <CheckCircle className="w-5 h-5 text-orange-500 flex-shrink-0" />
                    <span>{useCase}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Cross-links */}
        <section className="py-16 bg-muted/30">
          <div className="container mx-auto px-4">
            <h2 className="text-3xl font-bold text-center mb-8">
              Selling on Other UK Platforms?
            </h2>
            <div className="flex flex-wrap justify-center gap-4 max-w-3xl mx-auto">
              <Button asChild variant="outline">
                <Link to="/ai-image-generator-ebay-uk">AI for eBay UK</Link>
              </Button>
              <Button asChild variant="outline">
                <Link to="/ai-image-generator-depop">AI for Depop</Link>
              </Button>
              <Button asChild variant="outline">
                <Link to="/ai-image-generator-for-etsy">AI for Etsy UK</Link>
              </Button>
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-16">
          <div className="container mx-auto px-4">
            <h2 className="text-3xl font-bold text-center mb-12">
              Frequently Asked Questions
            </h2>
            <div className="max-w-3xl mx-auto space-y-6">
              {faqs.map((faq, index) => (
                <Card key={index} className="border-0 bg-gradient-to-br from-orange-500/5 to-amber-500/5">
                  <CardContent className="p-6">
                    <h3 className="font-semibold mb-2">{faq.question}</h3>
                    <p className="text-muted-foreground">{faq.answer}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 bg-muted/30">
          <div className="container mx-auto px-4">
            <div className="max-w-3xl mx-auto text-center">
              <h2 className="text-3xl font-bold mb-6">
                Start Creating Amazon UK Product Images Today
              </h2>
              <p className="text-xl text-muted-foreground mb-8">
                Join 5,000+ UK Amazon sellers using AI to create professional product photos. 
                No photography skills or expensive equipment needed.
              </p>
              <Button asChild size="lg" className="bg-gradient-to-r from-orange-500 to-amber-500 hover:opacity-90 text-white">
                <Link to="/try-generator">
                  Generate Amazon UK Images Free <ArrowRight className="ml-2 h-5 w-5" />
                </Link>
              </Button>
            </div>
          </div>
        </section>
      </main>
      
      <Footer />
    </>
  );
};

export default AIImageGeneratorAmazonUK;
